/**
 * @file Creative+ 顶级导航（内部代号 creative-assets）
 *
 * 接入形态：iframe 内嵌 https://ad.qq.com/agency/assets/creative-assets
 *   （目标页与 agency-fe 同为 ad.qq.com 主域下路径，CSP/X-Frame-Options 已放通）
 *
 * 品牌名 vs 代号：
 *   - 用户可见的产品品牌名保留『Creative+』（logo alt、气泡文案）
 *   - 代码内部标识统一为 creative-assets / creativeAssets（menu key/组件/CSS/后端字段/埋点）
 *
 * 权限模型：
 *   - is_creative_assets_enable 已全量上线，不再需要灰度判断
 *   - perm: 非 OA 用户命中以下任一角色可见（role_id 参考 permission-config.js#ROLES_NAME_TO_ID）：
 *     1 管理员 / 8 广告主代理协作者 / 100 代理商BOSS / 101 代理商管理员 /
 *     108 代理商协作者投放管理员 / 110 代理商主体超级管理员 / 111 代理商主体管理员 /
 *     114 代理商协作者运营管理员 / 116 设计师 / 117 子账号管理员
 *
 * 页面布局：
 *   - showSideNav: false —— 不渲染侧边导航
 *   - basePage: true + hideTabNav: true —— renderRoute 走 basePage 分支，
 *     跳过 .page-container 包裹（无 TabNav、无 padding-top）
 *     注意：跳过 .page-container 同时也跳过了它自带的 padding-top: 60px（给固定顶栏让位），
 *     因此 iframe 页面样式里需要自己补 padding-top / height 对齐顶栏，
 *     见 src/pages/creative-assets/style.less
 *   - 顶级即叶子（无 data 子数组）—— 不渲染二级/三级 SubNav
 *
 * 顶级 guidePopover "全新上线" 通过 label JSX 里包裹 GuidePopover 实现
 * （TopNavBar.js 顶级叶子分支未直接支持 guidePopover 字段，故走 label 内嵌方案，
 *  改动最小，不侵入 TopNavBar）。
 */

import React, { useEffect, useRef } from 'react';
import './creative-assets.less';
import { Loading } from '@tencent/ptp-fe-common';
import loadable from '@loadable/component';
import { isOa } from '@/utils';
import { hasCreativeAssetsEnabledRole } from './utils';
import GuidePopover from '@/adq-components/guide-popover';

const CREATIVE_ASSETS_TIP_STORAGE_KEY = 'creative_assets_new_nav_tip_v1';

const CreativeAssetsLabel: React.FC = () => {
  const ref = useRef<{ show: () => void; hide: () => void } | null>(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(CREATIVE_ASSETS_TIP_STORAGE_KEY)) {
        // 首次进入：延迟弹出气泡，避免初始渲染抖动
        const timer = setTimeout(() => {
          ref.current?.show();
          localStorage.setItem(CREATIVE_ASSETS_TIP_STORAGE_KEY, '1');
        }, 800);
        return () => clearTimeout(timer);
      }
    } catch (_e) {
      // localStorage 不可用时静默降级，不影响主流程
    }
  }, []);

  return (
    <GuidePopover
      ref={ref}
      placement="bottom"
      closable
      title="全新上线"
      content={
        <div>
          整合创意洞察、灵感、妙思制作、资产管理与报表五大能力，告别多工具来回切换，一站式完成灵感洞察到管理投放的创意全链路。
        </div>
      }
    >
      <span className="creative-assets-nav-wrapper">
        <img
          className="creative-nav-logo"
          src="https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/cm_creative_entry_default_1781504192649.svg?max_age=31536000"
          alt="Creative+"
        />
      </span>
    </GuidePopover>
  );
};

export default [
  {
    label: <CreativeAssetsLabel />,
    key: 'creativeAssets',
    href: '/creative-assets',
    // is_creative_assets_enable 已全量上线，红点恒定展示
    breathingLight: () => true,
    component: loadable(
      () => import(/* webpackChunkName: 'creative-assets' */ /* webpackMode: 'lazy' */ '@/pages/creative-assets'),
      { fallback: <Loading /> },
    ),
    basePage: true,
    showSideNav: false,
    hideTabNav: true,
    perm: (loginStatus: Record<string, any>) => {
      if (isOa()) return false;
      return hasCreativeAssetsEnabledRole(loginStatus);
    },
    'data-report-global': 'click_header_nav_creative_assets',
  },
];
